import { useMemo, useState } from "react";
import { Entity } from "@common/types/entity";
import { Button } from "@design-system/Button/Button";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import { CopyIcon } from "@icons";
import { useEntityEmailAddresses } from "@api/entity.api";
import { useWorkspaceEmailAliases } from "@api/workspaceEmailAlias.api";
import { EntitySelect } from "@components/entity/EntitySelect";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { useEntities } from "@hooks/useEntities";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { buildEntityForwardingAliases } from "./buildEntityForwardingAliases";
import styles from "./ForwardViaEmailModal.module.scss";

interface Props {
    show: boolean;
    onClose: () => void;
    mode: AccrualDocumentImporterMode;
}

export const ForwardViaEmailModal: React.FC<Props> = ({
    show,
    onClose,
    mode,
}) => {
    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const [selectedEntity, setSelectedEntity] = useState<Entity | undefined>();
    const addressesQuery = useEntityEmailAddresses(selectedEntity?.id);
    const aliasesQuery = useWorkspaceEmailAliases();
    const copyToClipboard = useCopyToClipboard();

    const rows = useMemo(() => {
        if (!selectedEntity) {
            return [];
        }
        return buildEntityForwardingAliases({
            entityUuid: selectedEntity.uuid,
            addresses: addressesQuery.data ?? null,
            aliases: aliasesQuery.data ?? [],
            kind: mode,
        });
    }, [addressesQuery.data, aliasesQuery.data, mode, selectedEntity]);

    const isLoading =
        !!selectedEntity &&
        (addressesQuery.isPending || aliasesQuery.isPending);

    return (
        <PlainModal show={show} onHide={onClose}>
            <StandardModalHeader>Forward via email</StandardModalHeader>
            <VStack gap={16}>
                <EntitySelect
                    entities={entities}
                    selectedEntity={selectedEntity}
                    onChange={setSelectedEntity}
                    placeholder="Select entity"
                    triggerButtonProps={{ variant: "primary", block: true }}
                />
                {!selectedEntity && (
                    <p className={styles.empty}>
                        Select an entity to see its forwarding addresses.
                    </p>
                )}
                {selectedEntity && isLoading && (
                    <p className={styles.empty}>Loading addresses...</p>
                )}
                {selectedEntity && !isLoading && rows.length === 0 && (
                    <p className={styles.empty}>
                        This entity has no forwarding addresses yet.
                    </p>
                )}
                {selectedEntity && !isLoading && rows.length > 0 && (
                    <ul className={styles.list}>
                        {rows.map((row) => (
                            <li key={row.id} className={styles.row}>
                                <span className={styles.label}>
                                    {row.label}
                                </span>
                                <span className={styles.address}>
                                    <span className={styles.email}>
                                        {row.email}
                                    </span>
                                    <Button
                                        variant="secondary"
                                        size="md"
                                        iconOnly
                                        aria-label={`Copy ${row.label} address`}
                                        onClick={() => {
                                            void copyToClipboard(row.email);
                                        }}
                                    >
                                        <CopyIcon />
                                    </Button>
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </VStack>
        </PlainModal>
    );
};
